import isNumber from 'lodash/isNumber';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { WrappedButton, WrappedButtonType } from '../ui/button';
import { ModalButtonActionType } from './ModalButtonActionType.enum';
import { ModalButtonShape } from './ModalButtonShape.proptypes';
import { ModalContext } from './ModalContext';

export class Modal extends Component {
	closeModal = buttonType => {
		const { modalId } = this.props;
		const { closeModal } = this.context;

		closeModal(modalId, buttonType);
	};

	// button click handler
	onButtonClick = button => {
		const { type, onClick } = button;

		if (typeof onClick === 'function') {
			// call the pass-in onClick handler and close the modal
			onClick(() => this.closeModal(type));
		} else {
			this.closeModal(type);
		}
	};

	// click event handler for the primary button
	onPrimaryButtonClick = () => {
		const primaryButton = this.buildPrimaryButton();

		this.onButtonClick(primaryButton);
	};

	// click event handler for the secondary button
	onSecondaryButtonClick = () => {
		const { secondaryButton } = this.props;
		this.onButtonClick(secondaryButton);
	};

	// onHide event handler.
	onHide = () => {
		const { primaryButton, onCloseButtonClick } = this.props;

		// if an onClose callback is set, execute it
		if (primaryButton && primaryButton.onClose) {
			primaryButton.onClose();
		}

		if (onCloseButtonClick) {
			onCloseButtonClick();
		}

		// treat 'Esc' or 'X' button the same as 'Ok' action
		this.closeModal(ModalButtonActionType.Ok);
	};

	// build style rules for dialog
	buildDialogStyle = () => {
		const { width, height } = this.props;
		const style = {
			width: isNumber(width) ? `${width}px` : width,
			height: isNumber(height) ? `${height}px` : height,
		};

		return style;
	};

	// default button when nothing is passed down
	buildPrimaryButton = () => {
		const { primaryButton } = this.props;

		return (
			primaryButton || {
				type: ModalButtonActionType.Ok,
				text: 'OK',
			}
		);
	};

	// render buttons for the modal footer
	renderFooter = () => {
		const { secondaryButton, supressPrimaryButton } = this.props;
		const primaryButton = this.buildPrimaryButton();

		return (
			<div className="dialog-footer">
				{secondaryButton && (
					<WrappedButton
						buttonType={
							secondaryButton.buttonType ?? WrappedButtonType.Secondary
						}
						disabled={secondaryButton.disabled}
						label={secondaryButton.text}
						icon={secondaryButton.icon}
						iconPos={secondaryButton.iconPos}
						onClick={this.onSecondaryButtonClick}
						dataqa={secondaryButton?.dataqa || 'modal-secondary'}
					/>
				)}

				{!supressPrimaryButton && (
					<WrappedButton
						buttonType={WrappedButtonType.Primary}
						disabled={primaryButton.disabled}
						label={primaryButton.text}
						onClick={this.onPrimaryButtonClick}
						dataqa={primaryButton?.dataqa || 'modal-primary'}
						loading={primaryButton.loading}
					/>
				)}
			</div>
		);
	};

	render() {
		const {
			size = 'medium',
			fullHeight = false,
			header,
			children,
			className,
			hideFooter,
			focusOnShow = true,
			dataqa,
		} = this.props;
		const contentStyle = this.buildDialogStyle();
		// Build the modal class name with size and fullHeight modifier
		const modalClassName = `modal ${size}-modal ${
			fullHeight ? 'full-height' : ''
		} ${className || ''}`;

		return (
			<Dialog
				className={modalClassName}
				style={contentStyle}
				header={header}
				showHeader
				visible
				modal
				footer={hideFooter ? null : this.renderFooter()}
				onHide={this.onHide}
				appendTo={document.getElementById('modals')}
				focusOnShow={focusOnShow}
				data-qa={dataqa}
				dataqa={dataqa}
			>
				{children}
			</Dialog>
		);
	}
}

// hook up Modal context
Modal.contextType = ModalContext;

// PropTypes
Modal.propTypes = {
	// unique modal id
	modalId: PropTypes.number.isRequired,

	// additional style class of the component
	className: PropTypes.string,

	// the title of the modal
	header: PropTypes.string,

	// primary button config
	primaryButton: ModalButtonShape,

	// secondary button config
	secondaryButton: ModalButtonShape,

	// removes footer from dialog
	hideFooter: PropTypes.bool,

	// the width of the dialog content
	width: PropTypes.number,

	// the height of the dialog content (header is not included)
	height: PropTypes.number,

	// modal sizes
	size: PropTypes.oneOf(['small', 'medium', 'large', 'sheet']),

	// full modal height modifier
	fullHeight: PropTypes.bool,
};
